<!-- eslint-disable vue/no-v-html -->
<template>
  <TransitionRoot as="template" :show="isModalOpen">
    <Dialog as="div" :initial-focus="component ? divRef : okButtonRef" class="relative z-50" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-50 overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden bg-white theme-border theme-radius theme-shadow px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:min-w-[300px] sm:max-w-max sm:p-8 overflow-y-auto"
              :class="modalClass"
            >
              <div v-if="isCloseButtonVisible" class="absolute top-0 right-0 hidden pt-5 pr-5 sm:block">
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-4 h-4 leading-none rounded-md hover:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  @click="hide(null)"
                >
                  <span class="sr-only">Close</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 fill-current" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" /></svg>
                </button>
              </div>

              <div class="sm:flex sm:items-start">
                <div class="mt-3 text-center sm:mt-0 sm:text-left">
                  <DialogTitle v-if="title" as="h3" class="text-lg font-medium leading-6" :class="{ 'text-error': isError }">{{ title }}</DialogTitle>
                  <div ref="divRef" class="mt-2">
                    <component
                      :is="component"
                      v-if="component"
                      :data="componentData"
                      @close="hide"
                    />
                    <p v-else class="opacity-80" :class="{ 'text-error': isError }" v-html="body" />
                  </div>
                </div>
              </div>

              <div v-if="!component" class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  ref="okButtonRef"
                  type="button"
                  class="text-white"
                  :class="isError ? 'btn-danger' : 'btn-primary'"
                  @click="okClickHandler"
                  v-html="okButton"
                />
                <button
                  v-if="cancelButton"
                  type="button"
                  class="btn-outline mr-2"
                  @click="hide"
                  v-html="cancelButton"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, shallowRef, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { Modal, OverlayModal } from '../composables/useModal';
// import { useFocus } from '@vueuse/core';

const props = defineProps({
  isOverlayModal: {
    type: Boolean,
    default: false
  }
});

const divRef = ref(null);

const isModalOpen = shallowRef(false);

const isCloseButtonVisible = ref(false);

const okButtonRef = ref(null);
const okClickHandler = shallowRef(null);
const afterHideHandler = shallowRef(null);

const title = shallowRef(null);
const body = shallowRef(null);
const validation = shallowRef(null);
const cancelButton = shallowRef('Cancel');
const okButton = shallowRef('Ok');

// Note: component must be passed as raw, use markRaw() to avoid Vue warning
const component = shallowRef(null);
const componentData = shallowRef(null);
const modalClass = shallowRef(null);
const isError = shallowRef(false);

function show() {
  isModalOpen.value = true;
}

function hide(payload = null) {
  isModalOpen.value = false;
  isError.value = false;
  if (afterHideHandler.value) {
    afterHideHandler.value(payload);
  }
}

function close() {
  if (!isCloseButtonVisible.value) {
    return;
  }
  hide(null);
}

function applyOptions(options = {}) {
  title.value = options.title || null;
  body.value = options.body || null;
  validation.value = options.validation || null;
  cancelButton.value = options.cancelButton ?? 'Cancel';
  okButton.value = options.okButton ?? 'Ok';
  component.value = options.component || null;
  componentData.value = options.componentData || null;
  afterHideHandler.value = options.afterHideHandler || null;
  modalClass.value = options.modalClass || null;
  isCloseButtonVisible.value = options.isCloseButtonVisible ?? true;
}

function alert(options = {}) {
  applyOptions(options);
  cancelButton.value = null;

  return new Promise(resolve => {
    okClickHandler.value = () => {
      resolve();
      hide();
    };

    afterHideHandler.value = () => {
      resolve();
    };

    show();
    // useFocus(okButtonRef, { initialValue: true });
  });
}

function error(txt) {
  if (isModalOpen.value) {
    const unwatch = watch(isModalOpen, val => {
      if (!val) {
        unwatch();
        error(txt);
      }
    });
    return null;
  }

  isError.value = true;
  return alert({
    title: 'Error',
    body: txt
  });
}

function confirm(options = {}) {
  applyOptions(options);

  return new Promise(resolve => {

    okClickHandler.value = () => {
      resolve(true);
      hide();
    };

    afterHideHandler.value = () => {
      resolve(false);
    };

    show();
    // useFocus(okButtonRef, { initialValue: true });
  });
}

function dialog(options = {}) {
  applyOptions(options);

  return new Promise(resolve => {
    afterHideHandler.value = payload => {
      resolve(payload);
    };

    show();
  });
}

if (props.isOverlayModal) {
  OverlayModal.alert = alert;
  OverlayModal.confirm = confirm;
  OverlayModal.dialog = dialog;
  OverlayModal.error = error;
} else {
  Modal.alert = alert;
  Modal.confirm = confirm;
  Modal.dialog = dialog;
  Modal.error = error;
}
</script>
